/**
 * Dashboard de operaciones SCC — cockpit cross-tenant.
 *
 * Vista de superadmin: agrega el estado de TODOS los tax-clients
 * (emisión DIAN, salud del sistema, entrega de webhooks/emails). Se
 * refresca en vivo cada 15s. Los no-superadmin reciben 403 y ven un
 * mensaje claro en vez de una pantalla vacía.
 *
 * Datos: GET /api/v1/admin/ops/stats (OpsStatsService, solo lectura).
 * Sin librerías de charts: el gráfico de 14 días es SVG con tokens.
 */

import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  AlertTriangle,
  Building2,
  CheckCircle2,
  Clock,
  Database,
  FileText,
  Mail,
  ShieldCheck,
  ShieldAlert,
  Webhook,
} from 'lucide-react'
import type { AxiosError } from 'axios'

import { opsApi, type OpsSnapshot, type OpsDayEmission } from '@/lib/api'

const REFRESH_MS = 15_000

export function DashboardPage() {
  const stats = useQuery({
    queryKey: ['ops-stats'],
    queryFn: () => opsApi.stats(),
    refetchInterval: REFRESH_MS,
    retry: false,
  })

  const forbidden = (stats.error as AxiosError | undefined)?.response?.status === 403

  return (
    <div className="mx-auto max-w-6xl pb-16">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-wider text-fg-muted">
            ▸ Operación · SCC
          </p>
          <h1 className="mt-1 text-3xl font-bold text-fg-primary">
            Centro de operaciones
          </h1>
          <p className="mt-2 text-sm text-fg-secondary">
            Estado en vivo de la plataforma Taxor — emisión DIAN, salud del
            sistema y entrega. Se actualiza cada 15 s.
          </p>
        </div>
        <LiveBadge
          isFetching={stats.isFetching}
          generatedAt={stats.data?.generated_at}
          hasError={!!stats.error && !forbidden}
        />
      </header>

      {forbidden && (
        <div className="rounded-xl border border-warning-soft-bd bg-warning-soft-bg p-6 text-sm text-warning-fg">
          <ShieldAlert className="mb-2 h-5 w-5" />
          Este panel es exclusivo de <strong>superadmins de SCC</strong>. Tu
          cuenta tiene rol admin de organización, sin acceso a la vista
          cross-tenant.
        </div>
      )}

      {stats.error && !forbidden && (
        <div className="rounded-xl border border-error-soft-bd bg-error-soft-bg p-6 text-sm text-error-fg">
          <AlertTriangle className="mb-2 h-5 w-5" />
          No se pudo cargar el estado operativo. Reintentando automáticamente…
        </div>
      )}

      {stats.isLoading && !stats.data && <DashboardSkeleton />}

      {stats.data && <DashboardBody data={stats.data} />}
    </div>
  )
}

// ─── Cuerpo ──────────────────────────────────────────────────────────

function DashboardBody({ data }: { data: OpsSnapshot }) {
  const { emission, clients, delivery, health, timeline } = data
  return (
    <div className="space-y-6">
      {/* Fila de KPIs hero */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          icon={FileText}
          label="Emitidos hoy"
          value={fmtInt(emission.docs_today)}
          hint={`${fmtInt(emission.docs_last_7_days)} en 7 días`}
          tone="brand"
        />
        <AcceptanceKpi rate={emission.acceptance_rate_24h} n={emission.transmitted_24h} />
        <KpiCard
          icon={Building2}
          label="Clientes activos"
          value={fmtInt(clients.active)}
          hint={
            clients.new_last_7_days > 0
              ? `+${fmtInt(clients.new_last_7_days)} esta semana`
              : 'sin altas esta semana'
          }
          tone="accent"
        />
        <KpiCard
          icon={Clock}
          label="En vuelo"
          value={fmtInt(emission.in_flight)}
          hint="draft + transmitiendo"
          tone={emission.in_flight > 0 ? 'warning' : 'muted'}
        />
      </div>

      {/* Gráfico de emisión 14 días + desglose de estados */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="rounded-xl border border-border bg-bg-surface p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-fg-primary">
              Emisión — últimos 14 días
            </h2>
            <Legend />
          </div>
          <EmissionChart series={timeline} />
        </section>

        <section className="rounded-xl border border-border bg-bg-surface p-6">
          <h2 className="mb-4 text-sm font-semibold text-fg-primary">
            Estados (histórico)
          </h2>
          <div className="space-y-3">
            <StateBar label="Aceptadas" value={emission.accepted} tone="success" total={statesTotal(emission)} />
            <StateBar label="Rechazadas" value={emission.rejected} tone="error" total={statesTotal(emission)} />
            <StateBar label="Fallidas" value={emission.failed} tone="warning" total={statesTotal(emission)} />
            <StateBar label="En vuelo" value={emission.in_flight} tone="brand" total={statesTotal(emission)} />
          </div>
          <div className="mt-5 rounded-lg border border-border bg-bg-app p-3">
            <p className="text-xs text-fg-muted">Valor aceptado (7 días)</p>
            <p className="mt-1 font-mono text-lg font-bold text-fg-primary">
              {fmtCOP(emission.gross_accepted_7d_cop)}
            </p>
            <p className="mt-0.5 text-[10px] text-fg-muted">aprox. COP · multi-moneda</p>
          </div>
        </section>
      </div>

      {/* Salud del sistema */}
      <section className="rounded-xl border border-border bg-bg-surface p-6">
        <h2 className="mb-4 text-sm font-semibold text-fg-primary">Salud del sistema</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <HealthTile
            icon={Activity}
            label="Ambiente DIAN"
            value={health.dian_environment === 'prod' ? 'Producción' : 'Habilitación'}
            sub={`modo ${health.transmitter_mode}`}
            tone={health.dian_environment === 'prod' ? 'brand' : 'muted'}
          />
          <HealthTile
            icon={health.dian_configured ? ShieldCheck : ShieldAlert}
            label="Transmisor DIAN"
            value={health.dian_configured ? 'Listo' : 'No configurado'}
            sub={health.dian_configured ? 'cert ECD cargado' : 'falta cert / password'}
            tone={health.dian_configured ? 'success' : 'warning'}
          />
          <CertTile days={health.cert_days_to_expiry} />
          <HealthTile
            icon={Database}
            label="Almacenamiento"
            value={health.storage_provider === 's3' ? 'S3' : 'Local'}
            sub={health.storage_provider === 's3' ? 'object storage' : 'filesystem (bind mount)'}
            tone={health.storage_provider === 's3' ? 'success' : 'muted'}
          />
        </div>
      </section>

      {/* Entrega + composición de clientes */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="rounded-xl border border-border bg-bg-surface p-6">
          <h2 className="mb-4 text-sm font-semibold text-fg-primary">Entrega</h2>
          <div className="grid grid-cols-2 gap-4">
            <DeliveryTile
              icon={Webhook}
              label="Webhooks en DLQ"
              value={delivery.webhook_dlq_depth}
              alert={delivery.webhook_dlq_depth > 0}
              okText="sin fallos"
            />
            <DeliveryTile
              icon={Webhook}
              label="Webhooks pendientes"
              value={delivery.webhooks_pending}
              alert={false}
              okText="cola vacía"
            />
            <DeliveryTile
              icon={Mail}
              label="Emails fallidos 24h"
              value={delivery.emails_failed_24h}
              alert={delivery.emails_failed_24h > 0}
              okText="todo entregado"
            />
            <DeliveryTile
              icon={Mail}
              label="Emails enviados 24h"
              value={delivery.emails_sent_24h}
              alert={false}
              okText=""
            />
          </div>
        </section>

        <section className="rounded-xl border border-border bg-bg-surface p-6">
          <h2 className="mb-4 text-sm font-semibold text-fg-primary">Clientes</h2>
          <div className="space-y-3">
            <StateBar label="Facturadores finales" value={clients.facturador_final} tone="brand" total={clients.total} />
            <StateBar label="ERP partners" value={clients.erp_partner} tone="accent" total={clients.total} />
            <StateBar label="Suspendidos" value={clients.suspended} tone="warning" total={clients.total} />
          </div>
          <p className="mt-4 text-xs text-fg-muted">
            {fmtInt(clients.total)} clientes en total ·{' '}
            {fmtInt(clients.active)} activos
          </p>
        </section>
      </div>
    </div>
  )
}

// ─── KPIs ────────────────────────────────────────────────────────────

type Tone = 'brand' | 'accent' | 'success' | 'warning' | 'error' | 'muted'

const TONE_TEXT: Record<Tone, string> = {
  brand: 'text-brand-600',
  accent: 'text-accent-600',
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-error',
  muted: 'text-fg-muted',
}

function KpiCard({
  icon: Icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: typeof FileText
  label: string
  value: string
  hint: string
  tone: Tone
}) {
  return (
    <div className="group rounded-xl border border-border bg-bg-surface p-5 transition-colors hover:border-brand-400">
      <div className="flex items-start justify-between">
        <Icon className={`h-5 w-5 ${TONE_TEXT[tone]}`} />
      </div>
      <div className="mt-4">
        <p className="text-3xl font-bold tracking-tight text-fg-primary tabular-nums">
          {value}
        </p>
        <p className="mt-1 text-sm text-fg-secondary">{label}</p>
        <p className="mt-0.5 text-xs text-fg-muted">{hint}</p>
      </div>
    </div>
  )
}

// Tasa de aceptación con anillo SVG.
function AcceptanceKpi({ rate, n }: { rate: number; n: number }) {
  const na = rate < 0
  const pct = na ? 0 : Math.round(rate)
  const tone: Tone = na ? 'muted' : pct >= 90 ? 'success' : pct >= 70 ? 'warning' : 'error'
  const r = 20
  const circ = 2 * Math.PI * r
  const dash = na ? 0 : (pct / 100) * circ
  return (
    <div className="rounded-xl border border-border bg-bg-surface p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-fg-secondary">Aceptación DIAN</p>
          <p className="text-xs text-fg-muted">últimas 24h</p>
        </div>
        <svg viewBox="0 0 52 52" className="h-14 w-14 -rotate-90">
          <circle cx="26" cy="26" r={r} fill="none" strokeWidth="6" className="stroke-border" />
          <circle
            cx="26"
            cy="26"
            r={r}
            fill="none"
            strokeWidth="6"
            strokeLinecap="round"
            className={
              tone === 'success'
                ? 'stroke-success'
                : tone === 'warning'
                  ? 'stroke-warning'
                  : tone === 'error'
                    ? 'stroke-error'
                    : 'stroke-border'
            }
            strokeDasharray={`${dash} ${circ}`}
            style={{ transition: 'stroke-dasharray 0.6s ease' }}
          />
        </svg>
      </div>
      <p className={`mt-2 text-3xl font-bold tabular-nums ${TONE_TEXT[tone]}`}>
        {na ? 'N/A' : `${pct}%`}
      </p>
      <p className="mt-0.5 text-xs text-fg-muted">
        {na ? 'sin transmisiones' : `${fmtInt(n)} transmitidas`}
      </p>
    </div>
  )
}

// ─── Gráfico de emisión (SVG barras apiladas) ────────────────────────

function EmissionChart({ series }: { series: OpsDayEmission[] }) {
  if (series.length === 0) {
    return <p className="py-12 text-center text-sm text-fg-muted">Sin datos.</p>
  }
  const max = Math.max(1, ...series.map((d) => d.total))
  const W = 100
  const H = 140
  const gap = 2
  const bw = (W - gap * (series.length - 1)) / series.length

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" style={{ height: 160 }}>
        {series.map((d, i) => {
          const x = i * (bw + gap)
          const totalH = (d.total / max) * (H - 8)
          const accH = (d.accepted / max) * (H - 8)
          const rejH = (d.rejected / max) * (H - 8)
          const otherH = totalH - accH - rejH
          const yOther = H - totalH
          return (
            <g key={d.day}>
              {accH > 0 && <rect x={x} y={H - accH} width={bw} height={accH} className="fill-success" rx="0.5" />}
              {rejH > 0 && <rect x={x} y={H - accH - rejH} width={bw} height={rejH} className="fill-error" rx="0.5" />}
              {otherH > 0 && <rect x={x} y={yOther} width={bw} height={otherH} className="fill-brand-400" rx="0.5" />}
              {totalH === 0 && <rect x={x} y={H - 0.6} width={bw} height={0.6} className="fill-border" />}
            </g>
          )
        })}
      </svg>
      <div className="mt-2 flex justify-between font-mono text-[10px] text-fg-muted">
        <span>{fmtDay(series[0].day)}</span>
        <span>{fmtDay(series[Math.floor(series.length / 2)].day)}</span>
        <span>hoy</span>
      </div>
    </div>
  )
}

function Legend() {
  return (
    <div className="flex items-center gap-3 text-[10px] text-fg-muted">
      <span className="flex items-center gap-1">
        <span className="h-2 w-2 rounded-sm bg-success" /> aceptadas
      </span>
      <span className="flex items-center gap-1">
        <span className="h-2 w-2 rounded-sm bg-error" /> rechazadas
      </span>
      <span className="flex items-center gap-1">
        <span className="h-2 w-2 rounded-sm bg-brand-400" /> otras
      </span>
    </div>
  )
}

// ─── Barras horizontales de estado ───────────────────────────────────

function StateBar({
  label,
  value,
  tone,
  total,
}: {
  label: string
  value: number
  tone: Tone
  total: number
}) {
  const pct = total > 0 ? (value / total) * 100 : 0
  const fill =
    tone === 'success'
      ? 'bg-success'
      : tone === 'error'
        ? 'bg-error'
        : tone === 'warning'
          ? 'bg-warning'
          : tone === 'accent'
            ? 'bg-accent-500'
            : 'bg-brand-500'
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-fg-secondary">{label}</span>
        <span className="font-mono font-semibold text-fg-primary tabular-nums">{fmtInt(value)}</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-bg-app">
        <div className={`h-full ${fill} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

// ─── Tiles de salud ──────────────────────────────────────────────────

function HealthTile({
  icon: Icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: typeof Activity
  label: string
  value: string
  sub: string
  tone: Tone
}) {
  return (
    <div className="rounded-lg border border-border bg-bg-app p-4">
      <Icon className={`h-4 w-4 ${TONE_TEXT[tone]}`} />
      <p className="mt-3 text-sm font-semibold text-fg-primary">{value}</p>
      <p className="text-xs text-fg-secondary">{label}</p>
      <p className="mt-0.5 text-[10px] text-fg-muted">{sub}</p>
    </div>
  )
}

function CertTile({ days }: { days: number }) {
  const none = days < 0
  const tone: Tone = none ? 'muted' : days < 15 ? 'error' : days < 45 ? 'warning' : 'success'
  return (
    <HealthTile
      icon={days >= 0 && days < 45 ? ShieldAlert : ShieldCheck}
      label="Cert ECD"
      value={none ? 'Sin cert' : `${days} días`}
      sub={none ? 'no cargado' : days < 45 ? 'renovar pronto' : 'vigencia OK'}
      tone={tone}
    />
  )
}

// ─── Entrega ─────────────────────────────────────────────────────────

function DeliveryTile({
  icon: Icon,
  label,
  value,
  alert,
  okText,
}: {
  icon: typeof Webhook
  label: string
  value: number
  alert: boolean
  okText: string
}) {
  return (
    <div
      className={`rounded-lg border p-4 ${
        alert ? 'border-error-soft-bd bg-error-soft-bg' : 'border-border bg-bg-app'
      }`}
    >
      <div className="flex items-center justify-between">
        <Icon className={`h-4 w-4 ${alert ? 'text-error' : 'text-fg-muted'}`} />
        {!alert && okText && <CheckCircle2 className="h-3.5 w-3.5 text-success" />}
      </div>
      <p className={`mt-2 text-2xl font-bold tabular-nums ${alert ? 'text-error-fg' : 'text-fg-primary'}`}>
        {fmtInt(value)}
      </p>
      <p className="text-xs text-fg-secondary">{label}</p>
      {!alert && okText && <p className="mt-0.5 text-[10px] text-fg-muted">{okText}</p>}
    </div>
  )
}

// ─── Live badge ──────────────────────────────────────────────────────

function LiveBadge({
  isFetching,
  generatedAt,
  hasError,
}: {
  isFetching: boolean
  generatedAt?: string
  hasError: boolean
}) {
  const dotClass = hasError
    ? 'bg-error'
    : isFetching
      ? 'bg-warning animate-pulse'
      : 'bg-success'
  return (
    <div className="flex flex-none items-center gap-2 rounded-full border border-border bg-bg-surface px-3 py-1.5">
      <span className={`h-2 w-2 rounded-full ${dotClass}`} />
      <span className="text-xs text-fg-secondary">
        {hasError ? 'Reintentando' : isFetching ? 'Actualizando' : 'En vivo'}
      </span>
      {generatedAt && !hasError && (
        <span className="font-mono text-[10px] text-fg-muted">
          {new Date(generatedAt).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </span>
      )}
    </div>
  )
}

// ─── Skeleton ────────────────────────────────────────────────────────

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-32 animate-pulse rounded-xl border border-border bg-bg-surface" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="h-56 animate-pulse rounded-xl border border-border bg-bg-surface lg:col-span-2" />
        <div className="h-56 animate-pulse rounded-xl border border-border bg-bg-surface" />
      </div>
    </div>
  )
}

// ─── Helpers de formato ──────────────────────────────────────────────

function fmtInt(n: number): string {
  return n.toLocaleString('es-CO')
}

function statesTotal(e: {
  accepted: number
  rejected: number
  failed: number
  in_flight: number
}): number {
  return Math.max(1, e.accepted + e.rejected + e.failed + e.in_flight)
}

function fmtCOP(raw: string): string {
  const n = Number(raw)
  if (!isFinite(n)) return '—'
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(n)
}

function fmtDay(iso: string): string {
  const [, m, d] = iso.split('-')
  return `${d}/${m}`
}
